// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { intersect } from '@tectonics/commons/arrays';
import { getSupportedTraits } from '@tectonics/prs/feature';
import { getSafeSupportedFeatureByName } from '@tectonics/prs/support';
import { handleError } from '@tectonics/alert-handler';
import { useScreenWidth } from '@tectonics/hooks';
import { useOpenedIncomingPayment, getSafeCheckoutPaymentOptions, getCountry } from '@tectonics/prs/incoming-payment';
import { getCreditCardCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment/checkout-payment-options';

import amexImageSrc from './images/credit-card-amex.svg';
import carnetImageSrc from './images/credit-card-carnet.svg';
import creditelImageSrc from './images/credit-card-creditel.svg';
import dinersImageSrc from './images/credit-card-diners.svg';
import discoverImageSrc from './images/credit-card-discover.svg';
import eloImageSrc from './images/credit-card-elo.svg';
import hipercardImageSrc from './images/credit-card-hipercard.svg';
import liderImageSrc from './images/credit-card-lider.svg';
import mastercardImageSrc from './images/credit-card-mastercard.svg';
import visaImageSrc from './images/credit-card-visa.svg';
import cabalImageSrc from './images/credit-card-cabal.svg';
import credimasImageSrc from './images/credit-card-credimas.svg';
import naranjaImageSrc from './images/credit-card-naranja.svg';
import magnaImageSrc from './images/credit-card-magna.svg';
import ocaImageSrc from './images/credit-card-oca.svg';
import walmartImageSrc from './images/credit-card-walmart.svg';
import bancardCheckImageSrc from './images/credit-card-bancard-check.svg';
import credifielcoImageSrc from './images/credit-card-credifielco.svg';
import infonetImageSrc from './images/credit-card-infonet.svg';
import jcbImageSrc from './images/credit-card-jcb.svg';
import unionpayImageSrc from './images/credit-card-unionpay.svg';
import rupayImageSrc from './images/credit-card-rupay.svg';
import privateLabelSrc from './images/private-label.svg';
import codensaImageSrc from './images/credit-card-codensa.png';
import verveImageSrc from './images/credit-card-verve.svg';
import troyImageSrc from './images/credit-card-troy.svg';

import lookAndFeel from '@tectonics/look-and-feel/volcanes';
import { Country } from '@ebanx/ebanx-js';

export function DisplayCardBrands(): Node {
  const screenWidth = useScreenWidth();
  const incomingPayment = useOpenedIncomingPayment();
  const country = getCountry(incomingPayment);
  const displayCardBrand = getDisplayCardBrandFeatureName(country, screenWidth);
  const checkoutPaymentOptions = getSafeCheckoutPaymentOptions(incomingPayment);
  const creditcardAllowedBrands = [
    ...getCreditCardCheckoutPaymentOptions(checkoutPaymentOptions),
    ...getExtraCreditCardBrandAllowedByCountry(country),
  ];
  const supportedDisplayCardBrandsFeature = getSafeSupportedFeatureByName(
    displayCardBrand,
    lookAndFeel.prsSupport
  );

  const supportedBrands = getSupportedTraits(supportedDisplayCardBrandsFeature);
  const availableBrands = intersect(supportedBrands, creditcardAllowedBrands);

  const amountOfBrandsToDisplay = screenWidth < 420 ? 5 : 6;
  const shouldDisplayMoreBrandsBadge = availableBrands.length > amountOfBrandsToDisplay && shouldShowMoreForCountry(country);
  const sliceIndex = shouldDisplayMoreBrandsBadge ? amountOfBrandsToDisplay - 1 : amountOfBrandsToDisplay;
  const brandsToDisplay = availableBrands.slice(0, sliceIndex);

  if (brandsToDisplay && brandsToDisplay.length) {
    return (
      <Root>
        {brandsToDisplay.map(brand => (
          <CardBrandImageWrapper key={brand}>
            <CardBrandImage src={getCardBrandImageSrc(brand)} alt={brand} />
          </CardBrandImageWrapper>
        ))}
        {shouldDisplayMoreBrandsBadge &&
          <CardBrandImageWrapper>
            <MoreBrandsBadge>
              +{availableBrands.length - sliceIndex}
            </MoreBrandsBadge>
          </CardBrandImageWrapper>
        }
      </Root>
    );
  }

  return null;
}

const Root = styled.div`
  height: 100%;
  display: flex;
`;

const CardBrandImageWrapper = styled.div`
  flex-grow: 1;
  padding-left: 4px;
`;

const CardBrandImage = styled.img`
  height: 100%;
  max-width: 30px;
  float: right;
`;

const MoreBrandsBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 5px;
  color: #666;
  font-size: 12px;
  font-weight: bold;
  border-radius: 2px;
  background-color: #ebebeb;
  font-family: ${props => props.theme.fontFamily.display};
`;

function getCardBrandImageSrc(cardBrand: string): string {
  switch (cardBrand) {
    case 'amex':
      return amexImageSrc;

    case 'carnet':
      return carnetImageSrc;

    case 'creditel':
      return creditelImageSrc;

    case 'diners':
      return dinersImageSrc;

    case 'discover':
      return discoverImageSrc;

    case 'elo':
      return eloImageSrc;

    case 'hipercard':
      return hipercardImageSrc;

    case 'lider':
      return liderImageSrc;

    case 'mastercard':
      return mastercardImageSrc;

    case 'visa':
      return visaImageSrc;

    case 'cabal':
      return cabalImageSrc;

    case 'credimas':
      return credimasImageSrc;

    case 'naranja':
      return naranjaImageSrc;

    case 'magna':
      return magnaImageSrc;

    case 'oca':
      return ocaImageSrc;

    case 'private':
      return privateLabelSrc;

    case 'walmart':
      return walmartImageSrc;

    case 'bancard-check':
      return bancardCheckImageSrc;

    case 'credifielco':
      return credifielcoImageSrc;

    case 'infonet':
      return infonetImageSrc;

    case 'jcb':
      return jcbImageSrc;

    case 'unionpay':
      return unionpayImageSrc;

    case 'codensa':
      return codensaImageSrc;

    case 'rupay':
      return rupayImageSrc;

    case 'verve':
      return verveImageSrc;

    case 'troy':
      return troyImageSrc;

    default: {
      handleError(new Error(`Display card brand: there is no source for "${cardBrand}"`));
      return '';
    }
  }
}

function getDisplayCardBrandFeatureName(country: Country, screenWidth: number): string {
  switch (country) {
    case 'py':
      return 'display_card_accepts_brand';
    case 'br':
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'pe':
    case 'cr':
    case 'gt':
    case 'sv':
    case 'pa':
    case 'do':
    case 'bo':
    case 'ec':
    case 'uy':
    case 'za':
    default:
      return screenWidth > 460 ? 'display_card_accepts_brand' : 'display_alternative_card_accepts_brand';
  }
}

function shouldShowMoreForCountry(country: Country): boolean {
  switch (country) {
    case 'py':
      return true;
    case 'br':
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'pe':
    case 'cr':
    case 'gt':
    case 'sv':
    case 'pa':
    case 'do':
    case 'bo':
    case 'ec':
    case 'uy':
    case 'za':
    default:
      return false;
  }
}

function getExtraCreditCardBrandAllowedByCountry(country: Country): string[] {
  switch (country) {
    case 'ng':
      return ['verve'];

    default:
      return [];
  }
}
